import { useEffect, useState } from "react";
import api from "../lib/api";

// Tiny module-level cache to avoid refetching the same id over and over
const CACHE = new Map(); // user_id -> {tier, tier_name, tier_label, score}
const PENDING = new Map(); // user_id -> Promise

let queue = new Set();
let timer = null;

function flushQueue() {
  if (!queue.size) return;
  const ids = [...queue].slice(0, 50);
  queue = new Set();
  timer = null;
  const promise = api
    .get("/reputation/bulk", { params: { user_ids: ids.join(",") } })
    .then(({ data }) => {
      for (const id of ids) {
        const entry = data[id] || { tier: "white", tier_name: "White Gem", tier_label: "New User", score: 0, is_staff: false, staff_role: null, cosmetics: { frame: null, title: null, gem_variant: null } };
        if (!entry.cosmetics) entry.cosmetics = { frame: null, title: null, gem_variant: null };
        if (typeof entry.is_staff !== "boolean") entry.is_staff = false;
        if (entry.staff_role === undefined) entry.staff_role = null;
        CACHE.set(id, entry);
      }
      return data;
    })
    .catch(() => {
      for (const id of ids) {
        CACHE.set(id, { tier: "white", tier_name: "White Gem", tier_label: "New User", score: 0, is_staff: false, staff_role: null, cosmetics: { frame: null, title: null, gem_variant: null } });
      }
      return {};
    });
  for (const id of ids) PENDING.set(id, promise);
}

function ensureFetch(id) {
  if (CACHE.has(id)) return Promise.resolve(CACHE.get(id));
  if (PENDING.has(id)) return PENDING.get(id);
  queue.add(id);
  if (!timer) timer = setTimeout(flushQueue, 60);
  // Return a deferred resolving when the queue flushes
  return new Promise((resolve) => {
    const check = () => {
      if (CACHE.has(id)) resolve(CACHE.get(id));
      else setTimeout(check, 80);
    };
    check();
  });
}

export default function useReputation(userId) {
  const [entry, setEntry] = useState(() => (userId && CACHE.get(userId)) || null);

  useEffect(() => {
    if (!userId) {
      setEntry(null);
      return;
    }
    let alive = true;
    ensureFetch(userId).then((e) => {
      if (alive) setEntry(e);
    });
    return () => { alive = false; };
  }, [userId]);

  return entry;
}

// Optional helper for components that already have the data inline
export function setReputationCache(userId, data) {
  if (userId && data) CACHE.set(userId, data);
}
